import React, { useState, useEffect, useContext, createContext } from 'react'
import { cognitoSignIn, cognitoSignOut, checkCognitoSession } from '../clients/userPool'
import { apiAsync } from '../common/helpers'
import { useRouter } from './useRouter'
import { LEGACY } from '../common/constants'
import {
  MINWAGE_DATABASE_VALUE,
  PAIDLEAVE_DATABASE_VALUE,
  POSTINGS_DATABASE_VALUE
} from '../app/constants'

const authContext = createContext()

const useProvideAuth = () => {
  const router = useRouter()
  const [user, setAuthUser] = useState(null)
  const [company, setAuthCompany] = useState(null)
  const [session, setAuthSession] = useState(null)

  const getCurrentUser = async (props) => {
    await apiAsync({
      api: '/users/current',
      method: 'GET'
    })
      .then((currentUser) => {
        setAuthUser(currentUser)
        if (currentUser.companyIds.length > 1 || currentUser.isAdmin) {
          props.cognitoLoginUser({
            user: currentUser,
            data: null,
            company: null
          })
          router.push('/selectCompany')
        } else {
          getIdentity({
            user: currentUser,
            selectedCompanyId: currentUser.companyIds[0],
            success: props.cognitoLoginUser,
            fail: props.cognitoLoginError
          })
        }
      })
      .catch((error) => {
        console.error('Error while trying to retrieve the current user', error)
        props.cognitoLoginError(error)
      })
  }

  const getIdentity = async ({
    user,
    selectedCompanyId,
    success,
    fail
  }) => {
    const companyInfo = apiAsync({
      api: `/companies/${selectedCompanyId}`,
      method: 'GET'
    })

    const mappedLegacyUser = mapUserToLegacyContract(user, selectedCompanyId)

    await Promise.all([companyInfo])
      .then((value) => {
        const companyResponse = value[0]
        const companyInfoValue = {
          ...companyResponse,
          _id: companyResponse._id,
          paidLeaveSubscriptionEndDate: companyResponse.paidLeave && companyResponse.paidLeave.renewDate
        }
        setAuthCompany(companyInfoValue)

        success({
          user: mappedLegacyUser,
          company: companyInfoValue
        })

        let redirectTo

        if (companyResponse.paidLeave.hasDashboardAccess && user.products.includes(PAIDLEAVE_DATABASE_VALUE)) {
          redirectTo = '/paidLeave'
        }

        if (companyResponse.minimumWage.hasDashboardAccess && user.products.includes(MINWAGE_DATABASE_VALUE)) {
          redirectTo = '/minWage'
        }

        if (companyResponse.postings.hasDashboardAccess && user.products.includes(POSTINGS_DATABASE_VALUE)) {
          redirectTo = '/compliance'
        }

        router.push(redirectTo)
      })
      .catch((error) => {
        console.error('Error while trying to stitch together identity', error)
        fail(error)
      })
  }

  const mapUserToLegacyContract = (user, selectedCompanyId) => ({
    ...user,
    banner_notifications: mapUserToLegacyBannerNotifications(user, selectedCompanyId),
    user_preferences: mapUserToLegacyUserPreferences(user, selectedCompanyId)
  })

  const mapUserToLegacyUserPreferences = (user, selectedCompanyId) => {
    const userCompany = user.companies.filter((company) => String(company.id) === String(selectedCompanyId)) || [{}]
    let emailOffsetDays = ''

    if (userCompany.minWagePreferences
      && userCompany.minWagePreferences.notifications
      && userCompany.minWagePreferences.notifications.email
      && userCompany.minWagePreferences.notifications.email.daysBeforeChange
    ) {
      emailOffsetDays = userCompany.minWagePreferences.notifications.email.daysBeforeChange.toString()
    }

    return [{
      name: LEGACY.EMAIL_NOTIFICATIONS,
      value: emailOffsetDays
    }]
  }

  const mapUserToLegacyBannerNotifications = (user, selectedCompanyId) => [{
    name: 'email_notifications',
    status: mapAlertBannerToLegacyContract(user, selectedCompanyId)
  }]

  const mapAlertBannerToLegacyContract = (user, selectedCompanyId) => {
    let result = ''
    const userCompany = user.companies.filter((company) => String(company.id) === String(selectedCompanyId)) || [{}]

    if (userCompany.minWagePreferences && userCompany.minWagePreferences.alertBanner) {
      if (userCompany.minWagePreferences.alertBanner.subscribed === true) {
        result = LEGACY.SUBSCRIBED
      } else if (userCompany.minWagePreferences.alertBanner.dismissed === true) {
        result = LEGACY.DISMISSED
      }
    }

    return result
  }

  const signIn = ({
    username,
    password,
    fail,
    props
  }) => cognitoSignIn({
    username,
    password,
    getCurrentUser,
    changePasswordRequired,
    fail,
    props
  })

  const changePasswordRequired = (username, props) => {
    props.cognitoLoginAttempt({ status: 'idle' })
    router.push(`/change-password?username=${encodeURIComponent(username)}`)
  }

  const resetPassword = (username) => {
    router.push(`/reset-password?username=${encodeURIComponent(username)}`)
  }

  const resendTemporalPassword = async (email, onFail) => {
    await apiAsync({
      api: `/users/${encodeURIComponent(email)}/resend-welcome`,
      method: 'POST'
    })
      .then(() => {
        router.push(`/change-password?username=${encodeURIComponent(email)}`)
      })
      .catch((error) => {
        if (error.message === 'Request sent too recently.') {
          router.push(`/change-password?username=${encodeURIComponent(email)}`)
        } else {
          onFail(error)
        }
      })
  }

  const signOut = () => {
    global.sessionStorage.clear()
    setAuthUser(false)
    setAuthCompany(false)
    setAuthSession(false)
    cognitoSignOut()
  }

  useEffect(() => {
    let subscribed = true
    if (subscribed) {
      const currentSession = checkCognitoSession()
      if (currentSession) {
        setAuthSession(currentSession)
        setAuthUser(JSON.parse(global.sessionStorage.getItem('user')))
        setAuthCompany(JSON.parse(global.sessionStorage.getItem('company')))
      } else {
        setAuthSession(false)
        setAuthUser(false)
        setAuthCompany(false)
      }
    }
    return () => {
      subscribed = false
    }
  }, [])

  return {
    user,
    company,
    session,
    signIn,
    signOut,
    getCurrentUser,
    getIdentity,
    resetPassword,
    resendTemporalPassword
  }
}

// eslint-disable-next-line func-style
export function ProvideAuth({ children }) {
  const auth = useProvideAuth()
  // eslint-disable-next-line react/jsx-filename-extension
  return <authContext.Provider value={auth}>{children}</authContext.Provider>
}

export const useAuth = () => useContext(authContext)
